// Sprachmemos aufnehmen (E2, Schritt 3; PLAN.md, Abschnitt 3, „Sprachmemos
// und Diktat“: Das Memo ist der Beleg, das Diktat die Bequemlichkeit).
//
//  - Aufgenommen wird mit MediaRecorder, am liebsten MP4 mit AAC (das
//    iPhone), sonst WebM (andere Browser). Gespeichert und gesendet wird das
//    Original (Plan E2, „Ich entscheide“).
//  - Jede Sekunde kommt ein Teilstück sofort in den Speicher am Gerät
//    (arbeitskopie.ts). Bricht die Aufnahme ab – Absturz, App geschlossen,
//    Akku leer –, ist bis dahin alles da, und beim nächsten Start wird
//    daraus das Memo (memosRetten).
//  - Die Dauer misst die App selbst: Safari schreibt keine in die MP4. Es
//    zählt nur die Zeit, in der wirklich aufgenommen wird. Am iPhone
//    pausiert die Aufnahme, solange das Handy gesperrt ist, und was in der
//    Zeit gesagt wird, fehlt (Proben E1, 18.10.2026) – deshalb bleibt der
//    Bildschirm während der Aufnahme wach (Screen Wake Lock), und die App
//    sagt deutlich „nicht sperren“. Kann das Gerät den Bildschirm nicht wach
//    halten, bleibt der Hinweis.
//  - Nach 15 Minuten (oder knapp 19 MB) stoppt die Aufnahme von selbst.
//  - Es läuft immer nur eine Aufnahme; die Oberfläche hört über
//    `useMemoAufnahme` zu. Solange sie läuft, hält sie eine Sperre über alle
//    Fenster der App (navigator.locks) – so rettet kein zweites Fenster ein
//    Memo, das gerade noch aufgenommen wird.

import { useSyncExternalStore } from "react";
import { DATEI_ARTEN, dateiArt, type MemoFormat } from "@happy-energy/kern";
import { fehlerMelden, toastZeigen } from "@happy-energy/oberflaeche";
import { memoBeenden, memoBeginnen, memosRetten, memoTeilSichern, SchonGesendet, type NotizOrt } from "./arbeitskopie.ts";

/** Höchstens 15 Minuten je Memo – dann stoppt die Aufnahme von selbst. */
export const MEMO_HOECHSTENS_SEKUNDEN = 15 * 60;
/** Knapp unter der Grenze des Datei-Eingangs (20 MB) – in 15 Minuten wird es auch bei hoher Qualität kaum so viel. */
export const MEMO_HOECHSTENS_BYTES = DATEI_ARTEN["audio/mp4"] - 1024 * 1024;
/** Alle so viele Millisekunden ein Teilstück. */
const TEILSTUECK_MS = 1000;
/** So lange wartet „Stopp“ höchstens auf das letzte Teilstück. */
const STOPP_WARTET_MS = 3000;

/** In dieser Reihenfolge gewünscht: MP4 mit AAC (iPhone), MP4, WebM. */
const WUNSCH_FORMATE = ["audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/webm;codecs=opus", "audio/webm"] as const;

/** Das Format, in dem dieses Gerät aufnimmt – leer, wenn keines davon geht. */
export function memoFormat(): string {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return "";
  return WUNSCH_FORMATE.find((art) => MediaRecorder.isTypeSupported(art)) ?? "";
}

/** Was der Vertrag daraus macht: „audio/mp4;codecs=…“ → „audio/mp4“. Null: ein Format, das der Server nicht annimmt. */
export function memoArt(mimeType: string): MemoFormat | null {
  const art = dateiArt(mimeType.trim().replace(/^video\//i, "audio/"));
  return art === "audio/mp4" || art === "audio/webm" ? art : null;
}

// --- Abspielen ---------------------------------------------------------------------------
//
// Seit 19.10.2026 (Augentest am iPhone: „Dieses Memo lässt sich hier nicht
// abspielen“). Die Ursache: Safari macht einen Blob aus IndexedDB unlesbar,
// sobald der Datensatz neu gespeichert wird – gleich nach der Aufnahme
// schreibt der Sender das Memo neu („hochgeladen“, „abgeschickt“), und der
// Blob, aus dem die Liste ihre Adresse (blob:…) gebaut hatte, war danach
// leer (WebKit-Fehler 235687; dateien.ts). Ein Ton lädt am iPhone erst beim
// Tippen auf „Abspielen“ – und scheiterte. Am Laptop (Chrome) gibt es das
// nicht.
//
// Deshalb liegen die Bytes jetzt als ArrayBuffer in „dateien“, und
// abgespielt wird ein frischer Blob im Arbeitsspeicher, mit dem Grund-Typ
// („audio/mp4“, ohne Codec-Zusatz). Die gespeicherte Datei und ihre
// Prüfsumme bleiben, wie sie sind. Den Blob holt die Oberfläche selbst
// (memos.tsx, `useTonAdresse` über `dateiZumAnsehen` – am Gerät oder, nach
// dem Senden, aus dem Büro); hier steht nur noch, welcher Typ es sein muss.

/** Der Typ für die Wiedergabe: immer der Grund-Typ – „audio/mp4;codecs=mp4a.40.2“ → „audio/mp4“. Unbekannt: MP4 (das iPhone). */
export function wiedergabeTyp(format: string, dateiTyp = ""): MemoFormat {
  return memoArt(format) ?? memoArt(dateiTyp) ?? "audio/mp4";
}

/** „0:08“, „12:30“ */
export function dauerZeigen(sekunden: number): string {
  const s = Math.max(0, Math.floor(sekunden));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// --- Den Bildschirm wach halten -----------------------------------------------------

/**
 * Hält den Bildschirm wach, solange `an`. Der Browser gibt die Sperre von
 * selbst frei, wenn die App in den Hintergrund geht – kommt sie zurück, wird
 * sie neu angefordert.
 */
class Wachhalter {
  private sperre: WakeLockSentinel | null = null;
  private an = false;

  /** true: Der Bildschirm bleibt wach. false: Das Gerät kann es nicht (dann bleibt nur der Hinweis). */
  async einschalten(): Promise<boolean> {
    this.an = true;
    return this.anfordern();
  }

  /** Nach der Rückkehr in den Vordergrund: neu anfordern, falls der Browser sie freigegeben hat. */
  async wieder(): Promise<boolean> {
    if (!this.an) return false;
    if (this.sperre && !this.sperre.released) return true;
    return this.anfordern();
  }

  async ausschalten(): Promise<void> {
    this.an = false;
    const s = this.sperre;
    this.sperre = null;
    if (s && !s.released) await s.release().catch(() => undefined);
  }

  private async anfordern(): Promise<boolean> {
    if (!("wakeLock" in navigator) || document.visibilityState !== "visible") return false;
    try {
      const s = await navigator.wakeLock.request("screen");
      // Inzwischen gestoppt: gleich wieder frei.
      if (!this.an) {
        await s.release().catch(() => undefined);
        return false;
      }
      this.sperre = s;
      return true;
    } catch {
      // Abgelehnt (Energiesparmodus, ältere iPhones in der installierten App) – dann bleibt der Hinweis.
      this.sperre = null;
      return false;
    }
  }
}

// --- Was die Oberfläche sieht ----------------------------------------------------------

export type AufnahmeStand =
  | { art: "aus" }
  /** Das Mikrofon wird angefragt – am iPhone steht jetzt womöglich „Erlauben?“ da. */
  | { art: "startet"; ort: string }
  | {
      art: "laeuft";
      ort: string;
      memoId: string;
      /** Gemessene Aufnahmezeit, ganze Sekunden. */
      sekunden: number;
      /** Der Bildschirm wird wach gehalten. */
      wach: boolean;
      /** Die App war zwischendurch versteckt (gesperrt, andere App) und die Aufnahme hat pausiert. */
      unterbrochen: boolean;
    }
  /** Gestoppt – das Memo wird gerade zusammengesetzt und gespeichert. */
  | { art: "speichert"; ort: string };

/** Woran die Oberfläche erkennt, ob eine Aufnahme zu ihrer Notiz gehört: das Foto oder die Station. */
export function ortSchluessel(ort: NotizOrt): string {
  return ort.fotoId ?? ort.stationId ?? "";
}

let stand: AufnahmeStand = { art: "aus" };
const zuhoerer = new Set<() => void>();

function setzen(neu: AufnahmeStand): void {
  stand = neu;
  for (const z of zuhoerer) z();
}

function abonnieren(melden: () => void): () => void {
  zuhoerer.add(melden);
  return () => zuhoerer.delete(melden);
}

/** Der Stand der Aufnahme jetzt – außerhalb von React (Tests). */
export function aufnahmeJetzt(): AufnahmeStand {
  return stand;
}

/** Der Stand der Aufnahme – für Knopf, Leiste und alles, was während der Aufnahme anders aussieht. */
export function useMemoAufnahme(): AufnahmeStand {
  return useSyncExternalStore(
    abonnieren,
    () => stand,
    () => stand,
  );
}

// --- Die laufende Aufnahme ---------------------------------------------------------------

interface Laufend {
  memoId: string;
  ort: string;
  rekorder: MediaRecorder;
  strom: MediaStream;
  wach: Wachhalter;
  istWach: boolean;
  /** Teilstücke bisher. */
  nr: number;
  bytes: number;
  /** Gezählte Aufnahmezeit bis `seit` (ms). */
  gezaehlt: number;
  /** Seit wann gezählt wird – null, solange die App versteckt ist. */
  seit: number | null;
  /** Wann die App versteckt wurde, und wie viele Teilstücke es da waren. */
  versteckt: { seit: number; nr: number } | null;
  unterbrochen: boolean;
  /** Die Schreibvorgänge der Teilstücke, nacheinander – beim Stoppen ist so alles drin. */
  schreiben: Promise<void>;
  uhr: number;
  /** Warum gestoppt wurde, wenn nicht von Hand. */
  grund: "zeit" | "groesse" | "fehler" | null;
  stoppt: boolean;
  abgeschlossen: boolean;
  sperreLoesen: () => void;
  sichtbarkeit: () => void;
}

let laufend: Laufend | null = null;
/** Zählt Starts – wird während „startet“ abgebrochen (Seite verlassen), gilt der alte Start nicht mehr. */
let startNr = 0;

const MELDUNGEN = {
  geraet: "Sprachmemos gehen in diesem Browser nicht. Bitte tippen oder mit dem Mikrofon der Tastatur diktieren.",
  verweigert:
    "Das Mikrofon ist nicht freigegeben. Tippe noch einmal auf „Sprachmemo“ und dann auf „Erlauben“ – in der App vom Home-Bildschirm fragt das iPhone oft bei jedem Start neu. Kommt keine Frage: Einstellungen → Apps → Safari → Mikrofon → „Fragen“.",
  keinMikrofon: "Kein Mikrofon gefunden. Bitte tippen oder diktieren.",
  belegt: "Das Mikrofon ist gerade belegt – etwa von einem Telefonat oder einer anderen App. Bitte danach noch einmal.",
  start: "Die Aufnahme ließ sich nicht starten – bitte noch einmal.",
  fotoWeg: "Dieses Foto gibt es nicht mehr.",
} as const;

/** Was der Mensch tun kann, wenn das Mikrofon nicht will – je nach Fehler des Browsers. */
export function mikrofonMeldung(fehler: unknown): string {
  const name = fehler instanceof DOMException || fehler instanceof Error ? fehler.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return MELDUNGEN.verweigert;
    case "NotFoundError":
    case "OverconstrainedError":
      return MELDUNGEN.keinMikrofon;
    case "NotReadableError":
    case "AbortError":
      return MELDUNGEN.belegt;
    default:
      return MELDUNGEN.start;
  }
}

function sperrName(memoId: string): string {
  return `he-memo-${memoId}`;
}

/** Die gemessene Aufnahmezeit in Sekunden. */
function sekundenVon(l: Laufend, jetzt = Date.now()): number {
  return (l.gezaehlt + (l.seit !== null ? jetzt - l.seit : 0)) / 1000;
}

function standZeigen(l: Laufend): void {
  if (laufend !== l || l.stoppt) return;
  setzen({ art: "laeuft", ort: l.ort, memoId: l.memoId, sekunden: Math.floor(sekundenVon(l)), wach: l.istWach, unterbrochen: l.unterbrochen });
}

function rekorderBauen(strom: MediaStream, mimeType: string): MediaRecorder {
  try {
    return new MediaRecorder(strom, { mimeType });
  } catch {
    // Manche Browser kennen das Format, mögen es aber nicht als Vorgabe – dann ihr eigenes.
    return new MediaRecorder(strom);
  }
}

/**
 * „Sprachmemo“: fragt nach dem Mikrofon und beginnt die Aufnahme an der
 * Notiz dieses Ortes. Gibt null zurück, wenn sie läuft – sonst, was der
 * Mensch tun kann (Mikrofon verweigert, belegt, Browser kann es nicht).
 */
export async function memoStarten(ort: NotizOrt): Promise<string | null> {
  if (stand.art !== "aus") return null;
  const gewuenscht = memoFormat();
  if (!gewuenscht || typeof navigator.mediaDevices?.getUserMedia !== "function") return MELDUNGEN.geraet;
  const schluessel = ortSchluessel(ort);
  const meinStart = ++startNr;
  setzen({ art: "startet", ort: schluessel });

  let strom: MediaStream;
  try {
    strom = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (fehler) {
    if (meinStart === startNr) setzen({ art: "aus" });
    return mikrofonMeldung(fehler);
  }
  const aufgeben = () => {
    for (const t of strom.getTracks()) t.stop();
    if (meinStart === startNr) setzen({ art: "aus" });
  };
  // Während der Frage nach dem Mikrofon wurde die Seite verlassen: nicht mehr aufnehmen.
  if (meinStart !== startNr) {
    aufgeben();
    return null;
  }
  try {
    const rekorder = rekorderBauen(strom, gewuenscht);
    const format = memoArt(rekorder.mimeType || gewuenscht);
    if (!format) throw new Error(`Sprachmemo: Format „${rekorder.mimeType}“ nimmt der Server nicht an.`);
    const memoId = await memoBeginnen(ort, format);
    if (meinStart !== startNr || !memoId) {
      aufgeben();
      return memoId ? null : MELDUNGEN.fotoWeg;
    }
    aufnehmen(memoId, schluessel, rekorder, strom);
    return null;
  } catch (fehler) {
    aufgeben();
    // Schon gesendet (seit Schritt 4): kein Fehler der App – sagen, wie es geht.
    if (fehler instanceof SchonGesendet) return fehler.message;
    fehlerMelden("doku", fehler);
    return MELDUNGEN.start;
  }
}

function aufnehmen(memoId: string, ort: string, rekorder: MediaRecorder, strom: MediaStream): void {
  const jetzt = Date.now();
  const l: Laufend = {
    memoId,
    ort,
    rekorder,
    strom,
    wach: new Wachhalter(),
    istWach: false,
    nr: 0,
    bytes: 0,
    gezaehlt: 0,
    seit: document.visibilityState === "hidden" ? null : jetzt,
    versteckt: null,
    unterbrochen: false,
    schreiben: Promise.resolve(),
    uhr: 0,
    grund: null,
    stoppt: false,
    abgeschlossen: false,
    sperreLoesen: () => undefined,
    sichtbarkeit: () => undefined,
  };
  laufend = l;

  // Die Sperre „Aufnahme läuft“ für andere Fenster der App (memosRetten).
  if (typeof navigator.locks?.request === "function") {
    navigator.locks
      .request(sperrName(memoId), () =>
        // Schon fertig, bevor die Sperre kam (sehr kurze Aufnahme): gleich wieder frei.
        l.abgeschlossen ? Promise.resolve() : new Promise<void>((los) => (l.sperreLoesen = los)),
      )
      .catch(() => undefined);
  }

  rekorder.ondataavailable = (e) => {
    if (e.data.size === 0) return;
    l.nr += 1;
    l.bytes += e.data.size;
    const nr = l.nr;
    const sekunden = sekundenVon(l);
    l.schreiben = l.schreiben
      .then(async () => {
        // Das Memo gibt es nicht mehr (Foto mitten in der Aufnahme gelöscht): nichts mehr aufnehmen.
        if (!(await memoTeilSichern(memoId, nr, e.data, sekunden))) memoStoppen();
      })
      .catch((fehler: unknown) => {
        fehlerMelden("doku", fehler);
        l.grund = "fehler";
        memoStoppen();
      });
    if (l.bytes >= MEMO_HOECHSTENS_BYTES) memoStoppen("groesse");
  };
  rekorder.onstop = () => void abschliessen(l);
  rekorder.onerror = () => {
    l.grund = "fehler";
    memoStoppen();
  };
  // Ein Anruf oder eine andere App nimmt das Mikrofon: Was bis dahin da ist, wird zum Memo.
  for (const spur of strom.getAudioTracks()) spur.onended = () => memoStoppen();

  l.sichtbarkeit = () => {
    const t = Date.now();
    if (document.visibilityState === "hidden") {
      if (l.seit !== null) l.gezaehlt += t - l.seit;
      l.seit = null;
      l.versteckt = { seit: t, nr: l.nr };
      return;
    }
    if (l.versteckt) {
      // Kamen derweil laufend Teilstücke, lief die Aufnahme weiter (Laptop, Android) – die Zeit zählt.
      // Am iPhone pausiert sie, solange es gesperrt ist: Dann fehlt, was in der Zeit gesagt wurde.
      const dauer = t - l.versteckt.seit;
      const teile = l.nr - l.versteckt.nr;
      if (teile >= 2 && teile * TEILSTUECK_MS >= dauer / 2) l.gezaehlt += dauer;
      else if (dauer > 1500) l.unterbrochen = true;
      l.versteckt = null;
    }
    l.seit = t;
    void l.wach.wieder().then((wach) => {
      l.istWach = wach;
      standZeigen(l);
    });
    standZeigen(l);
  };
  document.addEventListener("visibilitychange", l.sichtbarkeit);

  l.uhr = window.setInterval(() => {
    const sekunden = sekundenVon(l);
    if (sekunden >= MEMO_HOECHSTENS_SEKUNDEN) memoStoppen("zeit");
    else if (stand.art === "laeuft" && Math.floor(sekunden) !== stand.sekunden) standZeigen(l);
  }, 250);

  rekorder.start(TEILSTUECK_MS);
  standZeigen(l);
  void l.wach.einschalten().then((wach) => {
    l.istWach = wach;
    standZeigen(l);
  });
}

/**
 * „Stopp“ – oder von selbst nach 15 Minuten, bei knapp 19 MB, wenn das
 * Mikrofon weg ist. Das Memo wird danach zusammengesetzt und gespeichert;
 * die Oberfläche sieht solange „speichert“.
 */
export function memoStoppen(grund?: "zeit" | "groesse"): void {
  const l = laufend;
  if (!l || l.stoppt) return;
  l.stoppt = true;
  if (grund) l.grund = grund;
  setzen({ art: "speichert", ort: l.ort });
  // Bis hierher zählt die Zeit – das letzte Teilstück kommt gleich noch.
  if (l.seit !== null) l.gezaehlt += Date.now() - l.seit;
  l.seit = null;
  if (l.rekorder.state === "inactive") {
    void abschliessen(l);
    return;
  }
  try {
    l.rekorder.stop();
  } catch {
    void abschliessen(l);
    return;
  }
  // Kommt „stop“ vom Browser nicht, wird trotzdem gespeichert, was da ist.
  window.setTimeout(() => void abschliessen(l), STOPP_WARTET_MS);
}

/**
 * Verlässt der Mensch die Seite mit der Notiz, endet ihre Aufnahme – sie
 * wird gespeichert wie mit „Stopp“. Steht gerade noch die Frage nach dem
 * Mikrofon da, wird gar nicht erst aufgenommen.
 */
export function memoStoppenFuer(ort: string): void {
  if (stand.art === "startet" && stand.ort === ort) {
    startNr += 1;
    setzen({ art: "aus" });
    return;
  }
  if (laufend?.ort === ort) memoStoppen();
}

async function abschliessen(l: Laufend): Promise<void> {
  if (l.abgeschlossen) return;
  l.abgeschlossen = true;
  l.stoppt = true;
  if (l.seit !== null) l.gezaehlt += Date.now() - l.seit;
  l.seit = null;
  window.clearInterval(l.uhr);
  document.removeEventListener("visibilitychange", l.sichtbarkeit);
  for (const t of l.strom.getTracks()) t.stop();
  setzen({ art: "speichert", ort: l.ort });
  try {
    await l.wach.ausschalten();
    await l.schreiben;
    const memo = await memoBeenden(l.memoId, sekundenVon(l));
    if (!memo) {
      toastZeigen("Es wurde nichts aufgenommen.", "fehler");
    } else if (l.grund === "fehler") {
      toastZeigen(`Die Aufnahme brach ab – ${dauerZeigen(memo.dauerSekunden)} bis dahin gespeichert.`, "fehler");
    } else if (l.grund === "zeit") {
      toastZeigen(`15 Minuten erreicht – Sprachmemo ${dauerZeigen(memo.dauerSekunden)} gespeichert`);
    } else if (l.grund === "groesse") {
      toastZeigen(`Das Memo ist sehr groß geworden – ${dauerZeigen(memo.dauerSekunden)} gespeichert`);
    } else {
      toastZeigen(`Sprachmemo ${dauerZeigen(memo.dauerSekunden)} gespeichert`);
    }
  } catch (fehler) {
    // Die Teilstücke bleiben am Gerät – beim nächsten Start wird daraus das Memo (memosRetten).
    fehlerMelden("doku", fehler);
    toastZeigen("Das Memo ließ sich gerade nicht speichern – es wird beim nächsten Start nachgeholt.", "fehler");
  } finally {
    l.sperreLoesen();
    if (laufend === l) laufend = null;
    setzen({ art: "aus" });
  }
}

// --- Nach einem Abbruch -------------------------------------------------------------------

/** Läuft diese Aufnahme gerade noch – hier oder in einem anderen Fenster der App? */
async function laeuftNoch(memoId: string): Promise<boolean> {
  if (laufend?.memoId === memoId) return true;
  if (typeof navigator.locks?.request !== "function") return false;
  return navigator.locks.request(sperrName(memoId), { ifAvailable: true }, (sperre) => sperre === null);
}

let rettung: Promise<void> | null = null;

/**
 * Beim Start der App, einmal: Memos aus abgebrochenen Aufnahmen fertig
 * machen (arbeitskopie.ts, memosRetten) – mit einem kurzen Hinweis, damit
 * der Verkäufer weiß, dass nichts verloren ist.
 */
export function memosRettenBeimStart(): Promise<void> {
  rettung ??= memosRetten(laeuftNoch)
    .then((zahl) => {
      if (zahl === 1) toastZeigen("Ein abgebrochenes Sprachmemo ist gerettet");
      else if (zahl > 1) toastZeigen(`${zahl} abgebrochene Sprachmemos sind gerettet`);
    })
    .catch((fehler: unknown) => fehlerMelden("doku", fehler));
  return rettung;
}
